import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiPencilSimpleLine, PiTrash } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import activeLoadStatusIcon from "../../assets/images/Active_load.svg";
import inactiveStatusIcon from "../../assets/images/Inactive_icon.svg";
import prospectStatusIcon from "../../assets/images/Prospect_icon.svg";
import transporterIcon from "../../assets/images/transporterIcon.svg";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import NewTableComponent from "../../components/NewTableComponent";
import Stats from "../../components/Stats";
import {
  deleteCustomer,
  fetchCustomerOverview,
  fetchCustomersPaginated,
} from "../../store/Slices/CustomerSlice";
import {
  AppMainModules,
  CustomersPermission,
  CustomersSubModules,
} from "../../utils/app-constant";
import { dateMonthYear, hasPermission } from "../../utils/helper";
import ExportButton from "../../components/ExportButton";
import {
  activeStatsIcon,
  lateStatsIcon,
  plusIcon,
  prospectIcon,
} from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import "./Customer.css";
import "../../styles/Services.css";
import { STATUS_ID_TO_KEY, statusFilterOptions } from "./customerEnums";
import { useCustomerListingExport } from "./useCustomerListingExport";

const toText = (value) => String(value ?? "").trim();
const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const titleCase = (value) => {
  const text = String(value || "").replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "-";
};
const percent = (part, total) =>
  total ? `${((part / total) * 100).toFixed(1)}%` : "0%";
const resolveCoordinateText = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};
const toPositiveNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getMetric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const resolveStatus = (status) => {
  const statusNumber = Number(status);
  if (Number.isFinite(statusNumber) && STATUS_ID_TO_KEY[statusNumber]) {
    return STATUS_ID_TO_KEY[statusNumber];
  }

  return normalize(status);
};

const resolveGoodsValue = (goods) =>
  toText(goods?.title ?? goods?.name ?? goods?.label ?? goods?.slug ?? goods);

const resolveCostCenterName = (costCenter) => {
  if (!costCenter) return "-";
  if (typeof costCenter === "string") return costCenter.trim() || "-";
  if (typeof costCenter === "object") {
    return (
      toText(costCenter.name) ||
      toText(costCenter.title) ||
      toText(costCenter.cost_center_name) ||
      toText(costCenter.label) ||
      "-"
    );
  }
  return "-";
};

const buildCustomerSearchFilters = (filters = {}) => {
  const mapped = {};

  Object.entries(filters || {}).forEach(([filterName, filterObj]) => {
    const rawValue = filterObj?.value;
    const hasNoValue =
      rawValue === undefined ||
      rawValue === null ||
      rawValue === "" ||
      (Array.isArray(rawValue) && rawValue.length === 0);

    if (!filterObj?.filter_type || hasNoValue) return;

    let filterType = filterObj.filter_type;
    let filterValue = rawValue;

    if (filterType === "equal_to") filterType = "equals";

    if (
      (filterName === "headquarterFilter" ||
        filterName === "typesOfGoodsFilter") &&
      filterType === "one_of"
    ) {
      const values = Array.isArray(filterValue) ? filterValue : [filterValue];
      if (values.length === 1) {
        filterType = "equals";
        filterValue = values[0];
      }
    }

    mapped[filterName] = {
      filter_type: filterType,
      value: filterValue,
    };
  });

  return mapped;
};

const customerStatusMeta = (status) => {
  const normalized = normalize(status);

  if (normalized === "active") {
    return {
      color: "#299900",
      icon: (
        <img
          src={activeLoadStatusIcon}
          alt="Active status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Active",
    };
  }

  if (normalized === "inactive") {
    return {
      color: "#EF0000",
      icon: (
        <img
          src={inactiveStatusIcon}
          alt="Inactive status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Inactive",
    };
  }

  if (normalized === "prospect") {
    return {
      color: "#F2841B",
      icon: (
        <img
          src={prospectStatusIcon}
          alt="Prospect status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Prospect",
    };
  }

  return {
    color: "#8F95B2",
    icon: null,
    label: titleCase(status),
  };
};

const Customers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canViewCustomers = hasPermission({
    permissionSlug: CustomersPermission.View,
    menuSlug: CustomersSubModules.Customers,
    moduleSlug: AppMainModules.customers,
  });
  const canCreateCustomers = hasPermission({
    permissionSlug: CustomersPermission.Create,
    menuSlug: CustomersSubModules.Customers,
    moduleSlug: AppMainModules.customers,
  });
  const canUpdateCustomers = hasPermission({
    permissionSlug: CustomersPermission.Update,
    menuSlug: CustomersSubModules.Customers,
    moduleSlug: AppMainModules.customers,
  });
  const canDeleteCustomers = hasPermission({
    permissionSlug: CustomersPermission.Delete,
    menuSlug: CustomersSubModules.Customers,
    moduleSlug: AppMainModules.customers,
  });

  const {
    customerOverviewData,
    customerPaginatedData,
    customerPaginatedLoading,
    deleteCustomerLoading,
  } = useSelector((state) => state.customer);

  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const apiSearchFilters = useMemo(
    () => buildCustomerSearchFilters(searchFilters),
    [searchFilters],
  );

  const {
    hasActiveFilters,
    exportLoading,
    pdfExportLoading,
    excelExportLoading,
    handleExportPdf,
    handleExportExcel,
  } = useCustomerListingExport(searchText, apiSearchFilters);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  useEffect(() => {
    dispatch(fetchCustomerOverview());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchCustomersPaginated({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters: apiSearchFilters,
      }),
    );
  }, [dispatch, currentPage, limit, searchText, apiSearchFilters]);

  const apiRows = useMemo(
    () =>
      safeArray(customerPaginatedData?.results).map((item, index) => {
        const poc = safeArray(item?.poc);
        const primaryPoc =
          poc.find((entry) => Boolean(entry?.is_primary)) || poc[0] || {};
        const secondaryContacts = poc
          .filter((entry) => !entry?.is_primary)
          .map((entry, contactIndex) => ({
            id: entry?.id || `${item?.id || index}-secondary-${contactIndex}`,
            name: toText(entry?.name),
            contact: toText(entry?.contact),
            designation: toText(entry?.designation),
            email: toText(entry?.email),
          }));

        const customerGoods = safeArray(item?.customer_goods);
        const goodsForTable = customerGoods
          .map(resolveGoodsValue)
          .filter(Boolean);
        const goodsForForm = customerGoods
          .map((goods) => {
            const id = Number(goods?.id);
            return Number.isFinite(id) && id > 0
              ? id
              : resolveGoodsValue(goods);
          })
          .filter(Boolean);
        const customerProducts = safeArray(
          item?.customer_products || item?.products,
        ).map((product, productIndex) => ({
          id: product?.id || `${item?.id || index}-product-${productIndex}`,
          name: toText(product?.name),
          unit: product?.unit_id ?? product?.unit ?? "",
          productType: toText(product?.product_type),
          productValue: toText(product?.product_value),
          insuranceCoverType: toText(product?.insurance_cover_type),
          insuranceStatus: toText(product?.insurance_status),
          insuranceCoverValue: toText(product?.insurance_cover_value),
        }));

        const warehouses = safeArray(item?.warehouses).map(
          (warehouse, warehouseIndex) => ({
            id:
              warehouse?.id ||
              `${item?.id || index}-warehouse-${warehouseIndex}`,
            warehouseName: toText(warehouse?.warehouse_name ?? warehouse?.name),
            city: toText(warehouse?.city),
            contact: toText(warehouse?.contact),
            contactPersonName: toText(warehouse?.contact_person_name),
            contactPersonNumber: toText(warehouse?.contact_person_number),
            warehouseAddress: toText(
              warehouse?.address ?? warehouse?.warehouse_address,
            ),
            warehouseLatitude: resolveCoordinateText(
              warehouse?.lat,
              warehouse?.latitude,
              warehouse?.warehouse_lat,
              warehouse?.warehouse_latitude,
            ),
            warehouseLongitude: resolveCoordinateText(
              warehouse?.lng,
              warehouse?.longitude,
              warehouse?.warehouse_lng,
              warehouse?.warehouse_longitude,
            ),
            typesOfGoods: safeArray(warehouse?.warehouse_goods)
              .map((goods) => {
                const id = Number(goods?.id);
                return Number.isFinite(id) && id > 0
                  ? id
                  : resolveGoodsValue(goods);
              })
              .filter(Boolean),
          }),
        );

        const files = safeArray(item?.files);
        const status = resolveStatus(item?.status) || "prospect";
        const recordId =
          item?.id ?? `${item?.customer_id || "customer"}-${index}`;
        const customerId = toText(item?.customer_id) || `TPC-${recordId}`;

        const costCenterObj =
          item?.cost_center ||
          item?.costCenter ||
          (Array.isArray(item?.cost_centers) ? item.cost_centers[0] : null);
        const costCenterName = resolveCostCenterName(costCenterObj);

        return {
          id: customerId,
          recordId,
          name: toText(item?.name) || "-",
          costCenter: costCenterName,
          headquarter: toText(item?.headquarter),
          contact: toText(item?.contact) || "-",
          primaryContactPerson: toText(primaryPoc?.name) || "-",
          status,
          typesOfGoods: goodsForTable,
          attachmentsCount: files.length,
          updatedAt: item?.updated_at || item?.created_at || null,
          email: toText(item?.email),
          address: toText(item?.address),
          industryType: toText(item?.industry_type),
          formData: {
            customerId,
            customerName: toText(item?.name),
            contact: toText(item?.contact),
            email: toText(item?.email),
            headquarter: toText(item?.headquarter),
            address: toText(item?.address),
            addressLatitude: resolveCoordinateText(
              item?.lat,
              item?.latitude,
              item?.address_lat,
              item?.address_latitude,
            ),
            addressLongitude: resolveCoordinateText(
              item?.lng,
              item?.longitude,
              item?.address_lng,
              item?.address_longitude,
            ),
            industryType: toText(item?.industry_type),
            typesOfGoods: goodsForForm,
            status,
            primaryContact: {
              name: toText(primaryPoc?.name),
              contact: toText(primaryPoc?.contact),
              designation: toText(primaryPoc?.designation),
              email: toText(primaryPoc?.email),
            },
            secondaryContacts,
            noWarehouses: !(item?.has_warehouses && warehouses.length > 0),
            warehouses,
            products: customerProducts,
            attachments: files,
            payment: {
              billingFrequency: toText(item?.billing_frequency),
              paymentMethod: toText(item?.payment_method),
              paymentTerm: toText(item?.payment_terms),
              bank: toText(item?.bank_name),
              accountTitle: toText(item?.account_title),
              iban: toText(item?.iban_number),
            },
            notes: toText(item?.note),
          },
        };
      }),
    [customerPaginatedData],
  );

  const rows = useMemo(() => apiRows, [apiRows]);

  const selectedDeleteRow = useMemo(
    () => rows.find((item) => item.recordId === deleteId) || null,
    [rows, deleteId],
  );

  const selectedDeleteName = selectedDeleteRow?.name?.trim() || "this record";
  const deleteTitle = "Are you sure you want to delete this customer?";
  const deleteDescription = `Deleting "${selectedDeleteName}" will permanently remove this customer from your records. This action cannot be undone.`;

  const closeDeletePopup = () => {
    if (deleteCustomerLoading) return;
    setDeletePopup(false);
    setDeleteId(null);
  };

  const totalPages = toPositiveNumber(
    customerPaginatedData?.meta?.totalPages,
    1,
  );
  const totalItems = (() => {
    const metaCount = Number(customerPaginatedData?.meta?.totalItems);
    if (Number.isFinite(metaCount) && metaCount >= 0) return metaCount;
    const total = Number(customerPaginatedData?.total);
    if (Number.isFinite(total) && total >= 0) return total;
    return rows.length;
  })();

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [currentPage, totalPages]);

  const localStats = useMemo(() => {
    const totalFallback = totalItems || rows.length;
    const active = rows.filter((item) => item.status === "active").length;
    const inactive = rows.filter((item) => item.status === "inactive").length;
    const prospect = rows.filter((item) => item.status === "prospect").length;
    const last = rows
      .map((item) => item.updatedAt)
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    return { total: totalFallback, active, inactive, prospect, last };
  }, [rows, totalItems]);

  const totalCount = getMetric(
    customerOverviewData?.total_customer,
    localStats.total,
  );
  const activeCount = getMetric(
    customerOverviewData?.active?.count,
    localStats.active,
  );
  const inactiveCount = getMetric(
    customerOverviewData?.inactive?.count,
    localStats.inactive,
  );
  const prospectCount = getMetric(
    customerOverviewData?.prospect?.count,
    localStats.prospect,
  );

  const getPercentage = (value, fallbackPart, fallbackTotal) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return `${parsed.toFixed(1)}%`;
    return percent(fallbackPart, fallbackTotal);
  };

  const activePercentage = getPercentage(
    customerOverviewData?.active?.percentage,
    activeCount,
    totalCount,
  );
  const inactivePercentage = getPercentage(
    customerOverviewData?.inactive?.percentage,
    inactiveCount,
    totalCount,
  );
  const prospectPercentage = getPercentage(
    customerOverviewData?.prospect?.percentage,
    prospectCount,
    totalCount,
  );
  const lastUpdatedAt = customerOverviewData?.last_updated || localStats.last;

  const statsData = [
    {
      title: "Total Customers",
      value: totalCount,
      text: `Last updated : ${lastUpdatedAt ? dateMonthYear(lastUpdatedAt) : "--"}`,
      icon: (
        <img
          src={transporterIcon}
          alt="Total customer icon"
          width={46}
          height={46}
        />
      ),
    },
    {
      title: "Active",
      value: activeCount,
      text: (
        <span>
          <b>{activePercentage}</b> from total
        </span>
      ),
      icon: activeStatsIcon,
      className: "completed",
    },
    {
      title: "Inactive",
      value: inactiveCount,
      text: (
        <span>
          <b>{inactivePercentage}</b> from total
        </span>
      ),
      icon: lateStatsIcon,
      className: "late",
    },
    {
      title: "Prospect",
      value: prospectCount,
      text: (
        <span>
          <b>{prospectPercentage}</b> from total
        </span>
      ),
      icon: prospectIcon,
      className: "pending-delivery",
    },
  ];

  const columns = [
    {
      name: "Customer ID",
      key: "id",
      selector: (row) => (
        <span
          className={canViewCustomers ? "hyperlink" : ""}
          style={{
            textTransform: "uppercase",
            cursor: canViewCustomers ? "pointer" : "default",
          }}
          onClick={() => {
            if (!canViewCustomers) return;
            navigate(`/customers/details/${row.recordId}`);
          }}
        >
          {row.id}
        </span>
      ),
      tableColumn: false,
      filterName: "idFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "130px",
    },
    {
      name: "Name",
      key: "name",
      selector: (row) => {
        const name = row.name || "-";
        return (
          <OverlayTrigger placement="top" overlay={<Tooltip>{name}</Tooltip>}>
            <span
              className={
                canViewCustomers
                  ? "hyperlink customer_name_single_line"
                  : "customer_name_single_line"
              }
              style={{
                textTransform: "capitalize",
                cursor: canViewCustomers ? "pointer" : "default",
              }}
              onClick={() => {
                if (!canViewCustomers) return;
                navigate(`/customers/details/${row.recordId}`);
              }}
            >
              {name}
            </span>
          </OverlayTrigger>
        );
      },
      tableColumn: true,
      filterName: "nameFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "170px",
    },
    {
      name: "Cost Center",
      key: "costCenter",
      selector: (row) => row.costCenter || "-",
      tableColumn: true,
      filterName: "costCenterFilter",
      type: "text",
      checked: true,
      disabled: false,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "150px",
    },
    {
      name: "Contact Number",
      key: "contact",
      selector: (row) => row.contact || "-",
      tableColumn: true,
      filterName: "contactFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "180px",
    },
    {
      name: "Primary Contact Person",
      key: "primaryContactPerson",
      selector: (row) => (
        <span className="hyperlink" style={{ textTransform: "capitalize" }}>
          {row.primaryContactPerson || "-"}
        </span>
      ),
      tableColumn: true,
      filterName: "primaryContactPersonFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "185px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => {
        const meta = customerStatusMeta(row.status);

        return (
          <span
            className="transporter_status_cell"
            style={{ color: meta.color }}
          >
            {meta.icon}
            {meta.label}
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      type: "select",
      options: statusFilterOptions,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "110px",
    },
    {
      name: "Action",
      key: "actions",
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdateCustomers && (
            <button
              type="button"
              className="edit_btn"
              title="Edit"
              onClick={() => navigate(`/customers/details/${row.recordId}`)}
            >
              <PiPencilSimpleLine />
            </button>
          )}
          {canDeleteCustomers && (
            <button
              type="button"
              className="delete_btn"
              title="Delete"
              onClick={() => {
                setDeleteId(row.recordId);
                setDeletePopup(true);
              }}
            >
              <PiTrash />
            </button>
          )}
        </div>
      ),
      checked: true,
      disabled: false,
      hide: false,
      exportColumn: false,
      sortable: false,
      tableColumn: false,
      minWidth: "110px",
      grow: 0,
    },
  ];

  const handleDelete = async () => {
    if (deleteId === null || deleteId === undefined || deleteCustomerLoading)
      return;
    if (!canDeleteCustomers) {
      errorNotify("You do not have permission to delete customers.");
      return;
    }

    const targetId = Number(deleteId);
    if (!Number.isFinite(targetId)) {
      errorNotify("Invalid customer id");
      return;
    }

    try {
      await dispatch(deleteCustomer(targetId)).unwrap();
      successNotify("Customer deleted successfully");
      closeDeletePopup();
      dispatch(fetchCustomerOverview());

      if (rows.length === 1 && currentPage > 1) {
        setCurrentPage((previous) => Math.max(1, previous - 1));
        return;
      }

      dispatch(
        fetchCustomersPaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters: apiSearchFilters,
        }),
      );
    } catch (error) {
      errorNotify(error || "Failed to delete customer");
    }
  };

  return (
    <div>
      <Breadcrumbs values={["Customers"]} />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDelete}
        loading={deleteCustomerLoading}
        title={deleteTitle}
        description={deleteDescription}
      />

      <div className="access_view">
        <h2>Customers</h2>

        <div className="services_buttons">
          <ExportButton
            type="pdf"
            onClick={handleExportPdf}
            disabled={!hasActiveFilters || pdfExportLoading}
            loading={pdfExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          <ExportButton
            type="excel"
            onClick={handleExportExcel}
            disabled={!hasActiveFilters || excelExportLoading}
            loading={excelExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          {canCreateCustomers && (
            <button onClick={() => navigate("/customers/add")}>
              {plusIcon} Add Customer
            </button>
          )}
        </div>
      </div>

      <Stats statsData={statsData} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={rows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={customerPaginatedLoading}
        paginationLoading={customerPaginatedLoading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        tableClassName="transporters_table"
      />

      {!customerPaginatedLoading && rows.length === 0 && (
        <div className="add_trip_suggestion_div" style={{ marginTop: 0 }}>
          <h4>There is nothing here...</h4>
          <p>
            There is nothing to view right now, please add new customer data to
            get started.
          </p>
          {canCreateCustomers && (
            <Button
              submitText={
                <span className="d-flex align-items-center gap-2 justify-content-center">
                  {plusIcon} Add Customer
                </span>
              }
              type="button"
              shouldRow={false}
              isShowSubmitBtn
              isShowCancel={false}
              submitBtn={() => navigate("/customers/add")}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Customers;
